/** React Query hooks for the Audio Studio (keys under `['audio-studio', …]`). */
import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  audioStudioApi,
  type AttachmentsQuery,
  type LibraryTracksParams,
  type ListTracksParams,
  type StudioJob,
} from '@/api/audioStudioApi';
import { useAuth } from '@/contexts/AuthContext';
import { useStaffState } from '@/cowork/useIsStaff';

export const audioKeys = {
  all: ['audio-studio'] as const,
  presets: ['audio-studio', 'presets'] as const,
  tracks: (p: ListTracksParams) => ['audio-studio', 'tracks', p] as const,
  tracksAll: ['audio-studio', 'tracks'] as const,
  job: (id: string) => ['audio-studio', 'job', id] as const,
  members: ['audio-studio', 'team-members'] as const,
  attachments: (q: AttachmentsQuery) => ['audio-studio', 'attachments', q] as const,
  attachmentsAll: ['audio-studio', 'attachments'] as const,
  trends: (region: string, period: number) => ['audio-studio', 'trends', region, period] as const,
  trendVideos: (trendId: string, region: string) => ['audio-studio', 'trend-videos', trendId, region] as const,
  /** Prefix of every collections key — invalidate it after any collection change (list + details). */
  collectionsAll: ['audio-studio', 'collections'] as const,
  collections: (brandId: string | null = null) => ['audio-studio', 'collections', 'list', brandId] as const,
  collection: (id: string) => ['audio-studio', 'collections', 'detail', id] as const,
  library: (p: LibraryTracksParams) => ['audio-studio', 'library', 'tracks', p] as const,
  libraryFilters: ['audio-studio', 'library', 'filters'] as const,
  playlists: ['audio-studio', 'library', 'playlists'] as const,
  playlist: (id: string) => ['audio-studio', 'library', 'playlist', id] as const,
};

export const JOB_POLL_MS = 3000;

export function isJobActive(job: Pick<StudioJob, 'status'> | undefined): boolean {
  return !job || job.status === 'queued' || job.status === 'running';
}

/** Audio Studio is Beatly staff only for now: flip to `false` to open it to every `show_cowork` client. */
export const AUDIO_STUDIO_STAFF_ONLY = true;

/**
 * Who may use the Audio Studio (page, Ad Studio card, sidebar, search and the Soundtracks blocks on beats /
 * collaborations): the api-v2 `show_cowork` flag (plus the dev-only VITE_COWORK_FORCE override App.tsx uses) and,
 * while {@link AUDIO_STUDIO_STAFF_ONLY}, a Beatly staff session per Cowork `GET /v1/me`. `null` while staff is
 * unknown (loading), so nothing flashes for a client. In development an unreachable Cowork API counts as staff.
 */
export function useAudioStudioAccess(): boolean | null {
  const { user } = useAuth();
  const { staff, failed } = useStaffState();
  const cowork = user?.show_cowork === true || (import.meta.env.DEV && import.meta.env.VITE_COWORK_FORCE === 'true' && !!user);
  if (!cowork) return false;
  if (!AUDIO_STUDIO_STAFF_ONLY) return true;
  if (failed && import.meta.env.DEV) return true;
  return staff;
}

/** {@link useAudioStudioAccess} as a plain yes/no (unknown = no). */
export function useAudioStudioEnabled(): boolean {
  return useAudioStudioAccess() === true;
}

export function usePresets() {
  return useQuery({ queryKey: audioKeys.presets, queryFn: () => audioStudioApi.presets(), staleTime: 30 * 60_000, retry: 1 });
}

export function useStudioTracks(params: ListTracksParams, enabled = true) {
  return useQuery({ queryKey: audioKeys.tracks(params), queryFn: () => audioStudioApi.tracks(params), enabled });
}

/** Polls a job every 3 s while it is queued/running; stops on succeeded/failed. Shared by `useJob` and "Recent creations". */
export function jobQueryOptions(jobId: string) {
  return queryOptions({
    queryKey: audioKeys.job(jobId),
    queryFn: () => audioStudioApi.job(jobId),
    enabled: Boolean(jobId),
    refetchInterval: (q) => (isJobActive(q.state.data) ? JOB_POLL_MS : false),
    refetchIntervalInBackground: true,
    retry: 2,
    staleTime: 0,
  });
}

export function useJob(jobId: string | null | undefined) {
  return useQuery(jobQueryOptions(jobId || ''));
}

export function useTeamMembers(enabled: boolean) {
  return useQuery({ queryKey: audioKeys.members, queryFn: () => audioStudioApi.teamMembers(), enabled, staleTime: 10 * 60_000 });
}

export function useAttachments(q: AttachmentsQuery, enabled = true) {
  return useQuery({ queryKey: audioKeys.attachments(q), queryFn: () => audioStudioApi.attachments(q), enabled, retry: false });
}

export function useAudioTrends(region: string, period: number) {
  return useQuery({ queryKey: audioKeys.trends(region, period), queryFn: () => audioStudioApi.trends({ region, period }), staleTime: 10 * 60_000 });
}

/** Real TikTok songs (`cml-…` ids) have top videos; fixture / sample songs never do. */
export function trendHasVideoSource(trendId: string): boolean {
  return trendId.startsWith('cml-');
}

/**
 * A song's top TikTok videos (GET /trends/audio/{id}/videos). Shared by the trend cards (loaded once a card
 * scrolls into view) and the song drawer, so opening the drawer reuses what the card already fetched.
 */
export function useTrendVideos(trendId: string, region: string, enabled = true) {
  return useQuery({
    queryKey: audioKeys.trendVideos(trendId, region),
    queryFn: () => audioStudioApi.trendVideos(trendId, region),
    enabled: enabled && trendHasVideoSource(trendId),
    staleTime: 30 * 60_000,
  });
}

/** The team's collections (most recently updated first); `brandId` keeps only that brand's. */
export function useCollections(enabled = true, brandId: string | null = null) {
  return useQuery({
    queryKey: audioKeys.collections(brandId),
    queryFn: () => audioStudioApi.collections(brandId ? { brand_id: brandId } : {}),
    enabled,
  });
}

export function collectionQueryOptions(id: string) {
  return queryOptions({ queryKey: audioKeys.collection(id), queryFn: () => audioStudioApi.collection(id), enabled: Boolean(id), retry: false });
}

/** One collection with its sounds. */
export function useCollection(id: string | null) {
  return useQuery(collectionQueryOptions(id || ''));
}

export function useLibraryTracks(params: LibraryTracksParams, enabled = true) {
  return useQuery({ queryKey: audioKeys.library(params), queryFn: () => audioStudioApi.library.tracks(params), enabled, placeholderData: (prev) => prev });
}

export function useLibraryFilterOptions() {
  return useQuery({ queryKey: audioKeys.libraryFilters, queryFn: () => audioStudioApi.library.filterOptions(), staleTime: 30 * 60_000 });
}

export function usePlaylists() {
  return useQuery({ queryKey: audioKeys.playlists, queryFn: () => audioStudioApi.library.playlists(), staleTime: 10 * 60_000 });
}

export function usePlaylist(id: string | null) {
  return useQuery({ queryKey: audioKeys.playlist(id || ''), queryFn: () => audioStudioApi.library.playlist(id!), enabled: Boolean(id) });
}
